import styled, { css, keyframes } from 'styled-components';

const panelEnter = keyframes`
  0% {
    transform: translateY(20px) scale(0.97);
    opacity: 0;
  }
  60% {
    transform: translateY(-4px) scale(1.01);
    opacity: 1;
  }
  100% {
    transform: translateY(0) scale(1);
  }
`;

const panelExit = keyframes`
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  40% {
    transform: translateY(-4px) scale(0.98);
  }
  100% {
    transform: translateY(30px) scale(0.95);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const getPrimary = (theme: any) => theme?.config?.primary || '#0A51C5';
const getBG = (theme: any, dark: boolean) =>
  dark
    ? theme?.config?.background || '#1a1a1a'
    : theme?.config?.background || '#ffffff';

export const FloatingButton = styled.button<{
  isOpen: boolean;
  isDarkTheme: boolean;
}>`
  position: fixed;
  bottom: 28px;
  right: 28px;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  border: none;
  background: ${({ theme }) => getPrimary(theme)};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Soft elevated shadow */
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
  z-index: 1000;

  transition:
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px rgba(0, 0, 0, 0.32);
  }

  &:active {
    transform: scale(0.92);
  }

  svg {
    width: 26px;
    height: 26px;
    fill: white;

    path {
      fill: white !important;
    }
  }

  /* Subtle pulse when closed to attract attention */
  ${({ isOpen }) =>
    !isOpen &&
    css`
      animation: ${pulse} 2.4s ease-in-out infinite;
    `}
`;

export const NotificationBadge = styled.span<{ isDarkTheme: boolean }>`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ isDarkTheme }) => (isDarkTheme ? '#ff4444' : '#ff3333')};
  color: white;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(255, 68, 68, 0.4);
  border: 2px solid
    ${({ isDarkTheme }) => (isDarkTheme ? '#1a1a1a' : '#ffffff')};
  z-index: 1;
`;

export const ChatPanel = styled.div<{
  isOpen: boolean;
  isDarkTheme: boolean;
  shouldAnimate?: boolean;
}>`
  position: fixed;
  bottom: 12px;
  right: 0;
  width: 420px;
  height: 640px;
  max-height: 640px;
  margin-right: 18px;

  background: ${({ theme, isDarkTheme }) => getBG(theme, isDarkTheme)};
  border-top-left-radius: 18px;
  border-top-right-radius: 18px;
  border-bottom-left-radius: 18px;
  border-bottom-right-radius: 18px;

  /* soft glass shadow */
  box-shadow: 0 -6px 32px rgba(0, 0, 0, 0.22);

  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Smooth dynamic animation - only animate when state changes, not on initial mount */
  ${({ isOpen, shouldAnimate }) =>
    shouldAnimate
      ? css`
          animation: ${isOpen ? panelEnter : panelExit} 0.5s
            cubic-bezier(0.24, 0.82, 0.42, 1);
        `
      : css`
          /* On initial mount or when not animating, set final state immediately */
          transform: ${isOpen
            ? 'translateY(0) scale(1)'
            : 'translateY(30px) scale(0.95)'};
          opacity: ${isOpen ? 1 : 0};
        `}
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};

  @media (max-width: 600px) {
    width: 100%;
    max-height: 85vh;
    margin-right: 0;
    bottom: 0;
    border-radius: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

export const ChatHeader = styled.div<{ isDarkTheme: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 18px 22px;
  border-bottom: 1px solid
    ${({ isDarkTheme }) =>
      isDarkTheme ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'};

  background: ${({ isDarkTheme }) => (isDarkTheme ? '#161616' : '#fafafa')};
  backdrop-filter: blur(10px);
`;

export const UnreadCountBadge = styled.span<{ isDarkTheme: boolean }>`
  padding: 4px 10px;
  background: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 68, 68, 0.15)' : 'rgba(255, 68, 68, 0.1)'};
  color: ${({ isDarkTheme }) => (isDarkTheme ? '#ff6b6b' : '#ff4444')};
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
`;

export const HeaderTitle = styled.h3<{ isDarkTheme: boolean }>`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${({ isDarkTheme }) => (isDarkTheme ? '#ffffff' : '#2c2c2c')};
`;

export const CloseButton = styled.button<{ isDarkTheme: boolean }>`
  background: none;
  border: none;
  cursor: pointer;

  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  transition:
    background 0.22s,
    transform 0.2s;

  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)'};

  &:hover {
    transform: rotate(90deg);
    background: ${({ isDarkTheme }) =>
      isDarkTheme ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  }
`;

export const ChatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  position: relative;

  /* Debug: Add border to see if container is rendering */
  /* border: 2px solid red; */

  /* Ensure ChatSection can fill the space */
  > * {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  /* Fix Quill editor numbered list rendering issues */
  .ql-editor {
    /* Ensure editor itself doesn't clip content */
    overflow-y: auto;
    overflow-x: hidden;
    /* Add padding to prevent content from being cut at edges */
    padding-top: 12px;
    padding-bottom: 12px;

    /* Fix ordered lists - ensure proper spacing and no clipping */
    ol {
      margin: 0;
      /* Ensure enough space for two-digit numbers - increase padding significantly */
      /* Standard is usually 1.5em, but we need at least 2.5em for two-digit numbers */
      padding-left: 2.8em !important;
      list-style-position: outside;
      /* Add margin to prevent clipping at top */
      margin-top: 2px;
      margin-bottom: 2px;

      li {
        margin: 0;
        padding: 0;
        padding-left: 0.5em;
        /* Prevent text from being cut off vertically */
        padding-top: 2px;
        padding-bottom: 2px;
        line-height: 1.7;
        /* Ensure list markers are visible and not clipped */
        list-style-position: outside;
        /* Ensure proper display */
        display: list-item;
        /* Ensure content doesn't get cut - minimum height for proper rendering */
        min-height: 1.6em;
        /* Prevent any clipping */
        overflow: visible;
        position: relative;
      }

      /* Fix for two-digit numbers - ensure marker area is wide enough */
      /* Note: ::marker support varies, so we rely on padding-left above */
    }

    /* Additional fix: ensure no clipping on scrollable content */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 4px;
    }
  }
`;

export const ChatItem = styled.div<{
  isActive: boolean;
  isSelected: boolean;
  isDarkTheme: boolean;
}>`
  padding: 14px 22px;
  cursor: pointer;
  transition:
    background 0.25s ease,
    transform 0.18s ease,
    box-shadow 0.25s ease;

  background: ${({ isSelected, isDarkTheme }) =>
    isSelected
      ? isDarkTheme
        ? 'rgba(10, 81, 197, 0.22)'
        : 'rgba(10, 81, 197, 0.12)'
      : 'transparent'};

  border-left: 4px solid
    ${({ isSelected }) => (isSelected ? '#0A51C5' : 'transparent')};

  &:hover {
    ${({ isSelected, isDarkTheme }) =>
      !isSelected &&
      css`
        background: ${isDarkTheme
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.04)'};
        transform: translateY(-2px);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
      `}
  }
`;

export const AvatarPlaceholder = styled.div<{ isDarkTheme: boolean }>`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(10, 81, 197, 0.3)' : 'rgba(10, 81, 197, 0.12)'};

  color: ${({ theme }) => getPrimary(theme)};
  font-size: 15px;
  font-weight: 600;
`;

export const ChatName = styled.div<{ isDarkTheme: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ isDarkTheme }) => (isDarkTheme ? '#fff' : '#222')};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChatMessage = styled.div<{ isDarkTheme: boolean }>`
  font-size: 12px;
  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.6)'};

  overflow: hidden;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
`;

export const Overlay = styled.div<{ isClosing?: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;

  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} 0.35s
    cubic-bezier(0.25, 0.8, 0.4, 1);

  /* Prevent click-through during fade out */
  pointer-events: ${({ isClosing }) => (isClosing ? 'none' : 'auto')};
`;

export const BackButton = styled.button<{ isDarkTheme: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.22s,
    transform 0.2s;
  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)'};

  &:hover {
    background: ${({ isDarkTheme }) =>
      isDarkTheme ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const HeaderSubtitle = styled.div<{ isDarkTheme: boolean }>`
  font-size: 12px;
  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'};
  margin-top: 2px;
`;

export const EmptyState = styled.div<{ isDarkTheme: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)'};
`;

export const EmptyStateText = styled.div<{ isDarkTheme: boolean }>`
  margin-top: 16px;
  font-size: 14px;
  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)'};
`;

export const ChatListContainer = styled.div<{ isDarkTheme: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  display: flex;
  flex-direction: column;
`;

export const ChatListItem = styled.div<{ isDarkTheme: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  gap: 12px;
  border-bottom: 1px solid
    ${({ isDarkTheme }) =>
      isDarkTheme ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'};

  &:hover {
    background: ${({ isDarkTheme }) =>
      isDarkTheme ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'};
  }

  &:active {
    background: ${({ isDarkTheme }) =>
      isDarkTheme ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

export const ChatItemAvatar = styled.div`
  flex-shrink: 0;
`;

export const ChatItemContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ChatItemName = styled.div<{ isDarkTheme: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ isDarkTheme }) => (isDarkTheme ? '#fff' : '#222')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChatItemSubtitle = styled.div<{ isDarkTheme: boolean }>`
  font-size: 12px;
  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChatItemSubtitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
`;

export const ChatItemDateTime = styled.span<{ isDarkTheme: boolean }>`
  font-size: 12px;
  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)'};
  flex-shrink: 0;
  white-space: nowrap;
`;

export const ChatItemMessage = styled.span<{ isDarkTheme: boolean }>`
  font-size: 12px;
  color: ${({ isDarkTheme }) =>
    isDarkTheme ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'};
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ChatItemBadge = styled.div<{ isDarkTheme: boolean }>`
  flex-shrink: 0;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: ${({ theme }) => getPrimary(theme)};
  color: white;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;
